import { useEffect, useState } from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { formatCurrency, formatInteger } from "../../../../../formatters";
import { BrandDailyRow } from "../../../../../Brands/brandsApiMapping";
import { formatShortDisplayDate } from "../../../../mobileFormatters";
import MobileTablePagination from "../../../../MobileTablePagination";

const COLUMNS: Array<{ key: keyof BrandDailyRow; label: string; align: "left" | "right"; format: (row: BrandDailyRow) => string }> = [
  { key: "brand", label: "Brand", align: "left", format: (r) => r.brand },
  { key: "qualified", label: "Qualified", align: "right", format: (r) => formatInteger(r.qualified) },
  { key: "reported", label: "Reported", align: "right", format: (r) => formatInteger(r.reported) },
  { key: "paid", label: "Paid", align: "right", format: (r) => formatInteger(r.paid) },
  { key: "income", label: "Total income", align: "right", format: (r) => formatCurrency(r.income) },
  { key: "epq", label: "EPQ", align: "right", format: (r) => formatCurrency(r.epq) },
  { key: "epp", label: "EPP", align: "right", format: (r) => formatCurrency(r.epp) },
  { key: "failure", label: "Failure", align: "right", format: (r) => formatInteger(r.failure) },
  { key: "pending", label: "Pending", align: "right", format: (r) => formatInteger(r.pending) },
  { key: "initial", label: "Initial", align: "right", format: (r) => formatInteger(r.initial) },
];

const DATE_COL_WIDTH = 88;
const BRAND_COL_WIDTH = 140;
const DATA_COL_WIDTH = 88;
const GRID_TEMPLATE = `${DATE_COL_WIDTH}px ${BRAND_COL_WIDTH}px repeat(${COLUMNS.length - 1}, ${DATA_COL_WIDTH}px)`;
const DEFAULT_PAGE_SIZE = 10;

interface BrandsDailyTableProps {
  rows: BrandDailyRow[];
}

// Mobile's replacement for the desktop BrandsDailyTable.tsx: same columns/
// formatting/business logic, sticky Date column (the literal first column -
// Brand itself scrolls with the rest, matching the mockup), no sort controls
// (Story 4), but paginated (rows-per-page picker + prev/next, defaulting to
// 10/page like desktop) instead of rendering every row at once.
export default function BrandsDailyTable({ rows }: BrandsDailyTableProps) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = rows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <Paper variant="outlined" sx={{ p: "16px 14px", borderColor: "#ececec" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#1a1a1a" }}>Brands Performance - Daily</Typography>
      <Typography sx={{ fontSize: 12.5, color: "#9e9e9e", mt: 0.25, mb: 1.5 }}>Last 30 days</Typography>

      <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", border: "1px solid #ececec", borderRadius: "10px" }}>
        <Box sx={{ minWidth: DATE_COL_WIDTH + BRAND_COL_WIDTH + (COLUMNS.length - 1) * DATA_COL_WIDTH }}>
          <Box sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #ececec" }}>
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 2,
                p: "11px 12px",
                fontSize: 11,
                fontWeight: 700,
                color: "#8E24AA",
                backgroundColor: "#faf8fb",
                borderRight: "1px solid #f0e8f3",
              }}
            >
              Date
            </Box>
            {COLUMNS.map((column) => (
              <Box
                key={column.key}
                sx={{
                  p: "11px 8px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#8E24AA",
                  textAlign: column.align,
                  backgroundColor: "#faf8fb",
                }}
              >
                {column.label}
              </Box>
            ))}
          </Box>

          {rows.length === 0
            ? Array.from({ length: 4 }).map((_, index) => (
                <Box key={index} sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #f4f4f4" }}>
                  <Box sx={{ position: "sticky", left: 0, backgroundColor: "#fff", p: "11px 12px" }}>
                    <Skeleton variant="text" width="70%" />
                  </Box>
                  {COLUMNS.map((column) => (
                    <Box key={column.key} sx={{ p: "11px 8px" }}>
                      <Skeleton variant="text" />
                    </Box>
                  ))}
                </Box>
              ))
            : pagedRows.map((row, index) => (
                <Box key={`${row.date}-${row.brand}-${index}`} sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #f4f4f4" }}>
                  <Box
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: "#fff",
                      borderRight: "1px solid #f4f4f4",
                      p: "11px 12px",
                      fontSize: 12.5,
                      color: "#424242",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatShortDisplayDate(row.date)}
                  </Box>
                  {COLUMNS.map((column) => (
                    <Box
                      key={column.key}
                      sx={{
                        p: "11px 8px",
                        fontSize: 12.5,
                        textAlign: column.align,
                        fontWeight: column.key === "brand" ? 500 : 400,
                        color: "#616161",
                      }}
                    >
                      {column.format(row)}
                    </Box>
                  ))}
                </Box>
              ))}
        </Box>
      </Box>

      {rows.length > 0 && (
        <MobileTablePagination
          total={rows.length}
          pageSize={pageSize}
          page={safePage}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </Paper>
  );
}
